import { useEffect, useState, MutableRefObject } from 'react';
import { Chart as HChart, Point } from 'highcharts';
import { reportAPI } from 'api/report';
import {
    ContentSettings,
    DisplayMask,
    IKpiChartOverlay,
    IKpiChartOverlayDataRow,
    contentSettingsToComponentProperties,
    formatValueFull,
} from '@report-content';
import { Highcharts } from 'components/chart/Chart';
import { getOverlaySeries } from '../series/getOverlaySeries';
import { SeriesOverlayId, SeriesOverlayRightAxisId } from '../series';

export type OverlaysDataMap = { [overlayId: string]: Array<IKpiChartOverlayDataRow> };

interface UseKpiChartOverlaysParams {
    chart: MutableRefObject<HChart | undefined>;
    overlays: Array<IKpiChartOverlay>;
    isDataLoaded: boolean;
    contentSettings: ContentSettings;
    filtersString: string | null;
    // Resolved axis display masks (undefined = fall back to the axis' own default formatting).
    // These only affect the axis label formatters - series/tooltip values keep the backend-formatted values as-is.
    leftAxisDisplayMask?: DisplayMask;
    rightAxisDisplayMask?: DisplayMask;
    isChartReady: boolean;
    handlePointClick: (point: Point) => void;
    handlePointMouseOver: (point: Point, seriesId?: string) => void;
    handlePointMouseOut: () => void;
}

function makeAxisLabelFormatter(mask: DisplayMask | undefined) {
    return function (this: Highcharts.AxisLabelsFormatterContextObject) {
        if (!mask) {
            return this.axis.defaultLabelFormatter.call(this);
        }
        const v = Number(this.value);
        return mask.mask_type === 'date' && mask.date_format_string > ''
            ? Highcharts.dateFormat('%m/%d/%Y', v)
            : //@ts-ignore
              formatValueFull(v, mask);
    };
}

export function useKpiChartOverlays({
    chart,
    overlays,
    isDataLoaded,
    contentSettings,
    filtersString,
    leftAxisDisplayMask,
    rightAxisDisplayMask,
    isChartReady,
    handlePointClick,
    handlePointMouseOver,
    handlePointMouseOut,
}: UseKpiChartOverlaysParams) {
    const [overlaysData, setOverlaysData] = useState<OverlaysDataMap>({});
    const [isOverlayPopupOpen, setIsOverlayPopupOpen] = useState<boolean>(false);
    const [isOverlaysDataLoading, setIsOverlaysDataLoading] = useState<boolean>(false);

    const needsRightAxis = overlays.some((overlay) => overlay.axis === 'Right');

    const buildOverlaySeriesOptions = (
        overlay: IKpiChartOverlay,
        rows: Array<IKpiChartOverlayDataRow>,
    ): Highcharts.SeriesLineOptions => {
        const overlaySeries = getOverlaySeries(overlay, rows);
        overlaySeries.point = {
            events: {
                click: function () {
                    handlePointClick(this);
                },
                mouseOver: function () {
                    //@ts-ignore
                    handlePointMouseOver(this, this.series.id);
                },
                mouseOut: function () {
                    handlePointMouseOut();
                },
            },
        };
        return overlaySeries;
    };

    // Used only by the very first declarative chart build (getSeries in KpiChart.tsx), for
    // whatever overlays/overlaysData are already known at that point. Every subsequent change is
    // applied imperatively below, without touching the rest of the chart's options.
    const getOverlaySeriesList = (): Array<Highcharts.SeriesLineOptions> =>
        overlays.map((overlay) => buildOverlaySeriesOptions(overlay, overlaysData[overlay.id] ?? []));

    const syncRightAxisVisibility = () => {
        const axis = chart.current?.get(SeriesOverlayRightAxisId) as Highcharts.Axis | undefined;
        if (axis && axis.options.visible !== needsRightAxis) {
            axis.update({ visible: needsRightAxis }, false);
        }
    };

    const syncLegendVisibility = () => {
        if (!chart.current) {
            return;
        }
        const legendEligibleSeries = chart.current.series.filter((s) => s.options.showInLegend !== false);
        if (legendEligibleSeries.length === 1 && !legendEligibleSeries[0].visible) {
            // The legend is about to disappear with just this one series left. If the user had
            // hidden it by clicking its legend item earlier, force it back
            legendEligibleSeries[0].setVisible(true, false);
        }
        const shouldEnable = legendEligibleSeries.length !== 1;
        if (chart.current.legend.options.enabled !== shouldEnable) {
            chart.current.update({ legend: { enabled: shouldEnable } }, false);
        }
    };

    useEffect(() => {
        if (!overlays.length) {
            setOverlaysData({});
            return;
        }
        setIsOverlaysDataLoading(true);
        reportAPI
            .getKpiChartOverlaysData(
                contentSettings.elementId,
                overlays.map((overlay) => overlay.id),
                contentSettingsToComponentProperties(contentSettings),
                filtersString,
            )
            .then((newOverlaysData) => setOverlaysData(newOverlaysData))
            .catch(() => {})
            .finally(() => setIsOverlaysDataLoading(false));
    }, [overlays, filtersString]);

    // Apply the current overlays/overlaysData onto the *live* chart instance directly
    useEffect(() => {
        if (!chart.current || !isDataLoaded) {
            return;
        }
        try {
            const chartInstance = chart.current;
            const desiredIds = new Set(overlays.map((overlay) => overlay.id));

            chartInstance.series
                .filter((s) => typeof s.options.id === 'string' && s.options.id!.startsWith(`${SeriesOverlayId}_`))
                .filter((s) => !desiredIds.has((s.options.id as string).slice(SeriesOverlayId.length + 1)))
                .forEach((s) => s.remove(false));

            overlays.forEach((overlay) => {
                const seriesOptions = buildOverlaySeriesOptions(overlay, overlaysData[overlay.id] ?? []);
                const existing = chartInstance.get(seriesOptions.id as string) as Highcharts.Series | undefined;
                if (existing) {
                    existing.update(seriesOptions, false);
                } else {
                    chartInstance.addSeries(seriesOptions, false);
                }
            });

            chartInstance.yAxis[0]?.update(
                { labels: { formatter: makeAxisLabelFormatter(leftAxisDisplayMask) } },
                false,
            );
            (chartInstance.get(SeriesOverlayRightAxisId) as Highcharts.Axis | undefined)?.update(
                { labels: { formatter: makeAxisLabelFormatter(rightAxisDisplayMask) } },
                false,
            );

            syncRightAxisVisibility();
            syncLegendVisibility();
            chartInstance.redraw();
        } catch (e) {}
    }, [overlays, overlaysData, isDataLoaded, leftAxisDisplayMask, rightAxisDisplayMask, isChartReady]);

    return {
        getOverlaySeriesList,
        isOverlayPopupOpen,
        setIsOverlayPopupOpen,
        isOverlaysDataLoading,
    };
}
